"use client";

import { useState, useEffect } from "react";
import { getStudentList } from "@/lib/actions/student.action";
import { StudentProps } from "@/types/student";
import { ListProps } from "@/types/index";
import { cn } from "@/lib/utils";
import { Filter, Loader2 } from "lucide-react";
import { businessYear, businessMonth } from "@/lib/date";
import { ScrollHintArea } from "@/components/ui/scroll-hint-area";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const CURRENT_YEAR = businessYear();

export default function EnrolmentListTable() {
  const [selectedMonth, setSelectedMonth] = useState<number>(businessMonth());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [paginationData, setPaginationData] = useState<ListProps<StudentProps>>({
    success: true,
    total: 0,
    page_size: 10,
    next: null,
    previous: null,
    data: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEnrolments = async () => {
      setLoading(true);
      try {
        const result = await getStudentList({
          page: currentPage,
          searchQuery: "",
          status: "IN_PROGRESS",
          enrolment_month: selectedMonth,
          enrolment_year: CURRENT_YEAR,
        });
        setPaginationData(result);
      } catch (error) {
        console.error("Failed to fetch enrolments:", error);
        setPaginationData({
          success: false,
          total: 0,
          page_size: 10,
          next: null,
          previous: null,
          data: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEnrolments();
  }, [selectedMonth, currentPage]);

  // Reset to page 1 when month filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth]);

  const totalPages = Math.ceil(paginationData.total / paginationData.page_size);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const pageButtonClass = (active: boolean) =>
    cn(
      "min-w-9 cursor-pointer rounded px-2.5 py-1.5 text-sm font-medium transition-colors",
      active
        ? "bg-teal-50 dark:bg-teal-950/40 font-semibold text-teal-700 dark:text-teal-300"
        : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800",
    );

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="page-1" onClick={() => handlePageChange(1)} className={pageButtonClass(currentPage === 1)}>
          1
        </button>
      );

      if (startPage > 2) {
        pages.push(
          <span key="ellipsis-1" className="flex items-center px-2 text-sm text-slate-600 dark:text-slate-400">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={`page-${i}`} onClick={() => handlePageChange(i)} className={pageButtonClass(currentPage === i)}>
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis-2" className="flex items-center px-2 text-sm text-slate-600 dark:text-slate-400">
            ...
          </span>
        );
      }

      pages.push(
        <button key={`page-${totalPages}`} onClick={() => handlePageChange(totalPages)} className={pageButtonClass(currentPage === totalPages)}>
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-violet-600 dark:text-violet-300" aria-hidden />
          <label htmlFor="enrolment-month-filter" className="text-sm font-semibold text-slate-600 dark:text-slate-300">
            Filter by Enrolment Month · {CURRENT_YEAR}
          </label>
        </div>
        <select
          id="enrolment-month-filter"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-slate-900 dark:text-slate-100 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
        >
          {MONTHS.map((month) => (
            <option key={month.value} value={month.value}>
              {month.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 p-8 text-sm text-slate-600 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading enrolments…
        </div>
      ) : paginationData.data.length > 0 ? (
        <>
          <ScrollHintArea scrollClassName="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                    Full Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                    Starting Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                    Enrolment Grade
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                    Parent Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                    Contact Number
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginationData.data.map((student: any) => (
                  <tr key={student.id} className="border-b border-slate-100 dark:border-slate-800 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {student.fullname}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {student.new_enrolment_date ?? student.starting_date_first
                        ? new Date(student.new_enrolment_date ?? student.starting_date_first).toLocaleDateString("en-GB", { timeZone: "UTC" })
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {student.new_enrolment_grade ?? "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {student.parent_name ?? "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {student.parent_contact ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollHintArea>

          {totalPages > 1 && (
            <div className="flex justify-center sm:justify-end border-t border-slate-200 dark:border-slate-800 px-4 py-3">
              <div className="flex flex-wrap justify-center sm:justify-end items-center gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!paginationData.previous}
                  className={cn(
                    "rounded px-3 py-1.5 text-sm font-medium transition-colors",
                    paginationData.previous
                      ? "cursor-pointer bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-950/60"
                      : "cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
                  )}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">{renderPageNumbers()}</div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!paginationData.next}
                  className={cn(
                    "rounded px-3 py-1.5 text-sm font-medium transition-colors",
                    paginationData.next
                      ? "cursor-pointer bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-950/60"
                      : "cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="p-8 text-center text-sm text-slate-600 dark:text-slate-400">No enrolments found</div>
      )}
    </div>
  );
}
